import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { Entity, Region } from "../lib/types";
import { getRegionColor } from "../lib/regionColors";

interface EntitiesGridProps {
  entities: Entity[];
}

export function EntitiesGrid({ entities }: EntitiesGridProps): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation("entities");

  return (
    <section aria-labelledby="entities-grid-h" className="mb-8">
      {/* Section header — proto .card-h style */}
      <div className="flex items-center gap-2 mt-[22px] mb-3">
        <h2
          id="entities-grid-h"
          className="text-[12.5px] font-bold tracking-[.03em] uppercase text-muted"
        >
          All entities
        </h2>
        <span className="ml-auto text-[11px] font-semibold text-dim bg-surface-2 border border-border rounded-full px-[9px] py-0.5 tabular-nums">
          {entities.length}
        </span>
      </div>

      {entities.length === 0 ? (
        <p className="text-[13px] text-dim py-[18px] text-center border border-dashed border-border rounded-[9px]">
          {t("grid_empty")}
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[10px]">
          {entities.map((entity) => {
            const colorHex = entity.region
              ? getRegionColor(entity.region as Region, "hex")
              : null;

            return (
              <button
                key={entity.id}
                type="button"
                aria-label={`Open ${entity.name}`}
                onClick={() => navigate(`/entities/${entity.id}`)}
                className={[
                  "relative overflow-hidden flex flex-col gap-1 p-[13px_16px] text-left w-full min-h-[88px]",
                  "bg-surface border border-border rounded-[11px] shadow-card",
                  "transition-[box-shadow,transform,border-color] duration-[140ms]",
                  "hover:shadow-card-hov hover:-translate-y-px",
                ].join(" ")}
              >
                {/* Left accent bar */}
                {colorHex && (
                  <span
                    aria-hidden
                    className="absolute left-0 top-0 bottom-0 w-[3px] rounded-l-[11px]"
                    style={{ background: colorHex }}
                  />
                )}

                {/* Row 1: flag + country + region pill */}
                <div className="flex items-center gap-2 pl-1">
                  <span className="text-[16px] leading-none" aria-hidden>
                    {entity.country?.flag ?? "🏳"}
                  </span>
                  <span className="text-[12px] text-muted">
                    {entity.country?.name ?? "—"}
                  </span>
                  {entity.region && (
                    <span
                      className="ml-auto text-[10px] font-semibold tracking-[.04em] px-[7px] py-px rounded-full border"
                      style={{
                        color: colorHex ?? "var(--muted)",
                        borderColor: colorHex ? `${colorHex}44` : "var(--border)",
                        background: colorHex ? `${colorHex}18` : "var(--surface-2)",
                      }}
                    >
                      {entity.region}
                    </span>
                  )}
                </div>

                {/* Entity name */}
                <div className="text-[13.5px] font-semibold leading-[1.35] text-text mt-0.5 pl-1">
                  {entity.name}
                </div>

                {/* Legal form */}
                {entity.legalForm && (
                  <div className="text-[12px] text-muted leading-[1.35] pl-1">
                    {entity.legalForm}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
